import { useEffect, useRef, useState, type ReactNode } from "react";
import type { TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { FullscreenWindow, type WindowEntry } from "@/components/modal";
import { windowStack } from "@/app/windowStack";
import { EvalComparePanel } from "./compare/EvalComparePanel";
import { EvalRunDetailPanel } from "./EvalRunDetailPanel";
import type { EvalRunFilterChip } from "./evalRunFilterFields";
import { evalViewSlots } from "./evalView";
import { EvaluationResultsPanel } from "./EvaluationResultsPanel";
import { createEvalRunTableSource, DEFAULT_EVAL_RUN_PAGE_SIZE } from "./evalRunTableSourceFactory";
import type { EvaluationResultsWindowParams, EvaluationViewMode } from "./evaluationResultsWindow";

// 창 위에 열린 상세·비교·병합 side panel 의 상태다. URL 이 아니라 이 창의 로컬 상태다. 창은
// 라우터 밖에 있고 페이지 이동에도 유지된다. 그래서 URL 에 담으면 창과 URL 을 양방향으로 동기화해야 한다.
type EvalRunView =
  | { mode: "detail"; run: TrainingEvalRunSummaryView }
  | {
      mode: Exclude<EvaluationViewMode, "detail">;
      runs: readonly TrainingEvalRunSummaryView[];
      // 비교·병합의 행 색 슬롯이다.
      slotById: Readonly<Record<string, number>>;
    };

const EMPTY_SLOTS: Readonly<Record<string, number>> = {};

type EvaluationResultsWindowContentProps = {
  entry: WindowEntry;
  // 스택에서 이 엔트리가 전체화면으로 보이는지 나타낸다. false 면 마운트는 유지하고 창은 접는다.
  active: boolean;
};

// Evaluation Results 전체화면 창이다. params 는 열 때의 조회 스냅샷(scope·정책 조합·카탈로그 축)이다.
// 필터 칩·행 수·열린 뷰는 이 컴포넌트의 로컬 상태라서 최소화해도 유지된다. 정책 필터는 창의
// identity 이므로 열 때의 조합으로 고정한다(카드 표시에만 쓴다). 최소화하면 셸이 패널을
// 언마운트해서 폴링을 멈춘다(§12). 이 컴포넌트가 소스를 소유하므로 로드한 페이지는 보존된다.
export function EvaluationResultsWindowContent({
  entry,
  active,
}: EvaluationResultsWindowContentProps): ReactNode {
  const params = entry.params as EvaluationResultsWindowParams;
  const [chips, setChips] = useState<readonly EvalRunFilterChip[]>([]);
  const [pageSize, setPageSize] = useState(DEFAULT_EVAL_RUN_PAGE_SIZE);
  // 카드로 고른 초점 정책이다. 표를 그 정책의 run 으로만 좁힌다. null 이면 조합 전체를 표시한다.
  const [focusedPolicyId, setFocusedPolicyId] = useState<string | null>(null);
  const [view, setView] = useState<EvalRunView | null>(null);

  // 행 클릭 핸들러는 소스를 만들 때 고정된다. 그러나 최신 setView 를 호출해야 한다. 그래서 ref 를 거친다.
  const openDetailRef = useRef((run: TrainingEvalRunSummaryView) => {
    setView({ mode: "detail", run });
  });

  const [source] = useState(() =>
    createEvalRunTableSource({
      scope: params.scope,
      pageSize: DEFAULT_EVAL_RUN_PAGE_SIZE,
      baseFilters: params.catalogFilters,
      policies: params.policies,
      rowClickHandler: (run) => openDetailRef.current(run),
    }),
  );
  useEffect(() => () => source.dispose(), [source]);

  // 이전 딥링크가 전달한 초기 뷰다. 이미 열린 창을 딥링크로 다시 열면 opener 가 update 로 params 를
  // 교체한다. 그래서 참조가 바뀌면 다시 실행해 요청한 뷰로 전환한다. 사용한 즉시 params 에서
  // 지운다. params 는 열 때의 스냅샷으로 남는다. 그래서 이후의 무관한 params 갱신이 이전 뷰를
  // 다시 열지 않는다.
  const initialView = params.initialView;
  useEffect(() => {
    if (initialView === undefined) {
      return;
    }
    if (initialView.mode === "detail") {
      setView({ mode: "detail", run: initialView.runs[0] });
    } else {
      setView({
        mode: initialView.mode,
        runs: initialView.runs,
        slotById: evalViewSlots(initialView.runs.map((run) => run.id)),
      });
    }
    windowStack.update(entry.id, { params: { ...params, initialView: undefined } });
  }, [initialView, entry.id, params]);

  const openMultiRunView = (mode: "compare" | "merge") => {
    return (runs: readonly TrainingEvalRunSummaryView[]) => {
      setView({ mode, runs, slotById: evalViewSlots(runs.map((run) => run.id)) });
    };
  };
  const closeView = () => setView(null);

  return (
    <>
      <FullscreenWindow
        open={active}
        title={entry.title}
        // 패널 루트가 height 100% 그리드로 표 스크롤을 소유한다(§14). 셸은 높이만 제공한다.
        bodyLayout="fill"
        onMinimize={() => windowStack.minimize(entry.id)}
        onBackdrop={() => windowStack.minimize(entry.id)}
        onRequestClose={() => windowStack.close(entry.id)}
      >
        <EvaluationResultsPanel
          source={source}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          chips={chips}
          onChipsChange={setChips}
          baseFilters={params.catalogFilters}
          selectedPolicies={params.policies}
          focusedPolicyId={focusedPolicyId}
          onFocusPolicyChange={setFocusedPolicyId}
          onCompareRuns={openMultiRunView("compare")}
          onMergeRuns={openMultiRunView("merge")}
          viewSlots={view !== null && view.mode !== "detail" ? view.slotById : EMPTY_SLOTS}
        />
      </FullscreenWindow>

      {/* side panel(z 892)은 창(z 880) 위에 있다. 최소화하면 패널도 함께 접는다. 패널에는 별도의
          최소화 상태가 없다(§12). view 상태는 남으므로 복원하면 같은 뷰를 다시 연다. */}
      {active && view !== null ? (
        view.mode === "detail" ? (
          <EvalRunDetailPanel key={view.run.id} run={view.run} onClose={closeView} />
        ) : (
          <EvalComparePanel
            // 정렬한 key 를 쓴다. 그래서 같은 집합은 선택 순서와 관계없이 같은 콘텐츠가 되고,
            // 집합이 바뀌면 새로 만든다.
            key={`${view.mode}:${view.runs
              .map((run) => run.id)
              .sort()
              .join("+")}`}
            mode={view.mode}
            runs={view.runs}
            slotById={view.slotById}
            onClose={closeView}
          />
        )
      ) : null}
    </>
  );
}
